import { trpc } from '@/trpcClient';
import { Card } from '@shelflix/ui-web/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shelflix/ui-web/components/ui/table';
import { SimpleSwitch } from '@/components/ui/simple-switch';
import { Drawer } from '@/components/ui/drawer';
import { Button } from '@shelflix/ui-web/components/ui/button';
import Icon from '@shelflix/ui-web/components/Icon';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import type { inferProcedureOutput } from '@trpc/server';
import type { Router } from '@shelflix/trpc';
import { useUserStore } from '@/store/userStore';

type NotificationDefinition = inferProcedureOutput<Router['whatsappNotifications']['listDefinitions']>[number];

type TargetRoleConfig = { roleId: number; scope: string; label: string };

/**
 * Formats target role configs as bullet points for display
 */
function formatRecipients(targetRoleConfigs: unknown): string {
  if (!targetRoleConfigs || !Array.isArray(targetRoleConfigs) || targetRoleConfigs.length === 0) {
    return '—';
  }
  return (targetRoleConfigs as TargetRoleConfig[]).map((config) => `• ${config.label}`).join('\n');
}

export function DefinitionsTab() {
  const queryClient = useQueryClient();
  const user = useUserStore((s) => s.user);
  const isMaster = user?.isMaster ?? false;
  const [editingField, setEditingField] = useState<{ type: string; field: string } | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [pendingToggle, setPendingToggle] = useState<string | null>(null); // Track which toggle is being updated
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({}); // Track toggle states optimistically
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<NotificationDefinition | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: definitions, isLoading, isError } = useQuery(trpc.whatsappNotifications.listDefinitions.queryOptions());

  // Initialize toggle states when data loads
  React.useEffect(() => {
    if (definitions) {
      const initialStates: Record<string, boolean> = {};
      definitions.forEach((def) => {
        initialStates[def.notificationType] = def.isActive;
      });
      setToggleStates(initialStates);
    }
  }, [definitions]);

  // Auto-resize textarea when editing field changes
  useEffect(() => {
    if (textareaRef.current && editingField) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [editingField, tempValue]);

  // Mutation for updating global definition (template/description) - only for global admins
  const updateMutation = useMutation({
    ...trpc.whatsappNotifications.updateDefinition.mutationOptions(),
    onSuccess: () => {
      // Invalidate and refetch the definitions list
      queryClient.invalidateQueries({
        queryKey: ['whatsappNotifications', 'listDefinitions'],
      });
      toast.success('Definição atualizada com sucesso');
      setEditingField(null);
    },
    onError: (error) => {
      toast.error('Erro ao atualizar definição');
      logger.error('Failed to update WhatsApp notification definition', { error });
    },
  });

  // Mutation for updating tenant-specific config (isActive, targetRoleConfigs, targetUserIds)
  const updateTenantConfigMutation = useMutation({
    ...trpc.whatsappNotifications.updateTenantConfig.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappNotifications', 'listDefinitions'],
      });
      toast.success('Configuração atualizada com sucesso');
      setPendingToggle(null);
    },
    onError: (error) => {
      toast.error('Erro ao atualizar configuração');
      logger.error('Failed to update tenant notification config', { error });
      setPendingToggle(null);

      // Revert toggle state on error
      if (definitions) {
        const revertStates: Record<string, boolean> = {};
        definitions.forEach((def) => {
          revertStates[def.notificationType] = def.isActive;
        });
        setToggleStates(revertStates);
      }
    },
  });

  const handleStartEdit = (type: string, field: string, currentValue: string) => {
    // Only master users can edit template/description
    if (!isMaster) {
      toast.error('Apenas usuários master podem editar o template');
      return;
    }
    if (updateMutation.isPending) return; // Prevent edit during mutation
    setEditingField({ type, field });
    setTempValue(currentValue);
  };

  const handleSaveEdit = (type: string, field: string) => {
    updateMutation.mutate({
      notificationType: type,
      [field]: tempValue,
    });
  };

  const handleCancelEdit = () => {
    if (updateMutation.isPending) return; // Prevent cancel during mutation
    setEditingField(null);
    setTempValue('');
  };

  const handleToggleActive = (type: string, currentValue: boolean) => {
    setPendingToggle(type); // Mark this toggle as pending

    // Optimistically update the toggle state
    const newValue = !currentValue;
    setToggleStates((prev) => ({ ...prev, [type]: newValue }));

    // Use tenant config mutation for isActive toggle
    updateTenantConfigMutation.mutate({
      notificationType: type,
      isActive: newValue,
    });
  };

  const handleOpenDrawer = (definition: NotificationDefinition) => {
    setSelectedDefinition(definition);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedDefinition(null);
  };

  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-destructive text-sm">Erro ao carregar definições</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 md:px-0">
      {/* Desktop Table */}
      <Card className="hidden w-full overflow-hidden rounded-[10px] md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-neutral-600 bg-[var(--secondary)]">
              <TableHead className="!h-10 w-[15%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Tipo
              </TableHead>
              <TableHead className="!h-10 w-[25%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Descrição
              </TableHead>
              <TableHead className="!h-10 w-[25%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Template
              </TableHead>
              <TableHead className="!h-10 w-[25%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Destinatários
              </TableHead>
              <TableHead className="!h-10 w-[10%] overflow-hidden text-ellipsis">Ativa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Carregando...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !definitions?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <span className="text-muted-foreground text-sm">Nenhuma definição encontrada</span>
                </TableCell>
              </TableRow>
            ) : (
              definitions.map((def, index) => (
                <TableRow key={def.id} className={index % 2 === 1 ? 'bg-[var(--secondary)]' : ''}>
                  {/* Type */}
                  <TableCell className="!h-10 w-[15%] border-r border-neutral-600" title={def.notificationType}>
                    <div className="overflow-hidden font-mono text-xs break-words text-ellipsis">
                      {def.notificationType}
                    </div>
                  </TableCell>

                  {/* Description (editable) */}
                  <TableCell className="!h-10 w-[25%] border-r border-neutral-600">
                    {editingField?.type === def.notificationType && editingField?.field === 'description' ? (
                      <div className="flex gap-2">
                        <textarea
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          disabled={updateMutation.isPending}
                          className="flex-1 rounded border p-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                          rows={3}
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleSaveEdit(def.notificationType, 'description')}
                            disabled={updateMutation.isPending}
                            className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                            className="rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="group relative min-h-full cursor-pointer rounded p-1 text-xs"
                        onClick={() => handleStartEdit(def.notificationType, 'description', def.description)}
                        title={def.description || '-'}
                        style={{
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          const isDark = document.documentElement.classList.contains('dark');
                          const hoverColor = isDark ? 'var(--secondary-dark-hover)' : 'var(--secondary-hover)';
                          e.currentTarget.style.backgroundColor = hoverColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div className="min-h-full overflow-hidden break-words whitespace-normal">
                          {def.description || '-'}
                        </div>
                        <Icon
                          name="edit"
                          size={12}
                          className="pointer-events-none absolute top-1 right-1 opacity-30 transition-opacity group-hover:opacity-100"
                        />
                      </div>
                    )}
                  </TableCell>

                  {/* Template (editable) */}
                  <TableCell className="!h-10 w-[25%] border-r border-neutral-600">
                    {editingField?.type === def.notificationType && editingField?.field === 'template' ? (
                      <div className="flex gap-2">
                        <textarea
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          disabled={updateMutation.isPending}
                          className="flex-1 rounded border p-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-50"
                          rows={4}
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleSaveEdit(def.notificationType, 'template')}
                            disabled={updateMutation.isPending}
                            className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                            className="rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="group relative min-h-full cursor-pointer rounded p-1 font-mono text-xs"
                        onClick={() => handleStartEdit(def.notificationType, 'template', def.template)}
                        title={def.template}
                        style={{
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          const isDark = document.documentElement.classList.contains('dark');
                          const hoverColor = isDark ? 'var(--secondary-dark-hover)' : 'var(--secondary-hover)';
                          e.currentTarget.style.backgroundColor = hoverColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div className="min-h-full overflow-hidden break-words whitespace-pre-wrap">{def.template}</div>
                        <Icon
                          name="edit"
                          size={12}
                          className="pointer-events-none absolute top-1 right-1 opacity-30 transition-opacity group-hover:opacity-100"
                        />
                      </div>
                    )}
                  </TableCell>

                  {/* Recipients (read-only) */}
                  <TableCell className="!h-10 w-[25%] border-r border-neutral-600 align-top">
                    <div className="h-full py-2 text-xs whitespace-pre-line">
                      {formatRecipients(def.targetRoleConfigs)}
                    </div>
                  </TableCell>

                  {/* Active (toggle) */}
                  <TableCell className="!h-10 w-[10%]">
                    <div className="flex items-center justify-center">
                      <SimpleSwitch
                        checked={toggleStates[def.notificationType] ?? def.isActive}
                        onCheckedChange={() =>
                          handleToggleActive(def.notificationType, toggleStates[def.notificationType] ?? def.isActive)
                        }
                        disabled={pendingToggle === def.notificationType}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile Table */}
      <Card className="w-full overflow-hidden rounded-[10px] md:hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-neutral-600 bg-[var(--secondary)]">
              <TableHead className="!h-10 w-[70%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Tipo
              </TableHead>
              <TableHead className="!h-10 w-[30%] overflow-hidden text-ellipsis">Mais</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Carregando...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !definitions?.length ? (
              <TableRow>
                <TableCell colSpan={2} className="h-24 text-center">
                  <span className="text-muted-foreground text-sm">Nenhuma definição encontrada</span>
                </TableCell>
              </TableRow>
            ) : (
              definitions.map((def, index) => (
                <TableRow key={def.id} className={index % 2 === 1 ? 'bg-[var(--secondary)]' : ''}>
                  {/* Type */}
                  <TableCell className="!h-10 w-[70%] border-r border-neutral-600" title={def.notificationType}>
                    <div className="overflow-hidden font-mono text-xs break-words text-ellipsis">
                      {def.notificationType}
                    </div>
                  </TableCell>

                  {/* Details - Button to open drawer */}
                  <TableCell className="!h-10 w-[30%]">
                    <div className="flex items-center justify-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="btn-theme-ghost h-8 w-8"
                        onClick={() => handleOpenDrawer(def)}
                      >
                        <Icon name="clarify" variant="outlined" size={20} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile Details Drawer */}
      <Drawer open={drawerOpen} onOpenChange={handleCloseDrawer}>
        {selectedDefinition && (
          <div className="flex flex-col gap-6 p-4">
            <div>
              <h3 className="mb-6 text-lg font-semibold">Detalhes da Notificação</h3>

              <div className="space-y-6">
                {/* Tipo de Notificação */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Tipo de Notificação
                  </label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 break-words dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <p className="font-mono text-sm break-words">{selectedDefinition.notificationType}</p>
                  </div>
                </div>

                {/* Descrição (editable) */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Descrição</label>
                  {editingField?.type === selectedDefinition.notificationType &&
                  editingField?.field === 'description' ? (
                    <div className="space-y-2">
                      <textarea
                        ref={textareaRef}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        disabled={updateMutation.isPending}
                        className="w-full resize-none rounded-lg border border-neutral-300 p-3 text-sm disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600"
                        style={{
                          minHeight: '80px',
                          height: 'auto',
                          overflow: 'hidden',
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = 'auto';
                          target.style.height = `${target.scrollHeight}px`;
                        }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(selectedDefinition.notificationType, 'description')}
                          disabled={updateMutation.isPending}
                          className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="md:hidden">✓</span>
                          <span className="hidden md:inline">✓ Salvar</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={updateMutation.isPending}
                          className="flex-1 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="md:hidden">✕</span>
                          <span className="hidden md:inline">✕ Cancelar</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="group relative cursor-pointer rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 break-words dark:border-neutral-600 dark:bg-[var(--secondary-dark)]"
                      onClick={() =>
                        handleStartEdit(
                          selectedDefinition.notificationType,
                          'description',
                          selectedDefinition.description
                        )
                      }
                      style={{
                        transition: 'background-color 0.2s ease',
                      }}
                      onTouchStart={(e) => {
                        const isDark = document.documentElement.classList.contains('dark');
                        const hoverColor = isDark ? 'var(--secondary-dark-hover)' : 'var(--secondary-hover)';
                        e.currentTarget.style.backgroundColor = hoverColor;
                      }}
                      onTouchEnd={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                      }}
                    >
                      <p className="text-sm break-words">{selectedDefinition.description || '-'}</p>
                      <Icon
                        name="edit"
                        size={14}
                        className="pointer-events-none absolute top-2 right-2 opacity-30 transition-opacity group-active:opacity-100"
                      />
                    </div>
                  )}
                </div>

                {/* Template (editable) */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Template</label>
                  {editingField?.type === selectedDefinition.notificationType && editingField?.field === 'template' ? (
                    <div className="space-y-2">
                      <textarea
                        ref={textareaRef}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        disabled={updateMutation.isPending}
                        className="w-full resize-none rounded-lg border border-neutral-300 p-3 font-mono text-sm disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600"
                        style={{
                          minHeight: '120px',
                          height: 'auto',
                          overflow: 'hidden',
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = 'auto';
                          target.style.height = `${target.scrollHeight}px`;
                        }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(selectedDefinition.notificationType, 'template')}
                          disabled={updateMutation.isPending}
                          className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="md:hidden">✓</span>
                          <span className="hidden md:inline">✓ Salvar</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={updateMutation.isPending}
                          className="flex-1 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="md:hidden">✕</span>
                          <span className="hidden md:inline">✕ Cancelar</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="group relative cursor-pointer rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 break-words dark:border-neutral-600 dark:bg-[var(--secondary-dark)]"
                      onClick={() =>
                        handleStartEdit(selectedDefinition.notificationType, 'template', selectedDefinition.template)
                      }
                      style={{
                        transition: 'background-color 0.2s ease',
                      }}
                      onTouchStart={(e) => {
                        const isDark = document.documentElement.classList.contains('dark');
                        const hoverColor = isDark ? 'var(--secondary-dark-hover)' : 'var(--secondary-hover)';
                        e.currentTarget.style.backgroundColor = hoverColor;
                      }}
                      onTouchEnd={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                      }}
                    >
                      <p className="font-mono text-sm break-words whitespace-pre-wrap">{selectedDefinition.template}</p>
                      <Icon
                        name="edit"
                        size={14}
                        className="pointer-events-none absolute top-2 right-2 opacity-30 transition-opacity group-active:opacity-100"
                      />
                    </div>
                  )}
                </div>

                {/* Recipients (read-only) */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Destinatários
                  </label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <p className="text-sm whitespace-pre-line">
                      {formatRecipients(selectedDefinition.targetRoleConfigs)}
                    </p>
                  </div>
                </div>

                {/* Status Ativo/Inativo */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
                  <div className="flex items-center justify-between rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <span className="text-sm font-medium">
                      {(toggleStates[selectedDefinition.notificationType] ?? selectedDefinition.isActive)
                        ? 'Notificação Ativa'
                        : 'Notificação Inativa'}
                    </span>
                    <SimpleSwitch
                      checked={toggleStates[selectedDefinition.notificationType] ?? selectedDefinition.isActive}
                      onCheckedChange={() => {
                        handleToggleActive(
                          selectedDefinition.notificationType,
                          toggleStates[selectedDefinition.notificationType] ?? selectedDefinition.isActive
                        );
                      }}
                      disabled={pendingToggle === selectedDefinition.notificationType}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
